import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Avatar from "@mui/material/Avatar";
import { Badge, Divider } from "@mui/material";
import { ExpandLess, ExpandMore, Logout, Settings } from "@mui/icons-material";
import { deepPurple } from "@mui/material/colors";
import { authService } from "../../services/authService";
import { storageService } from "../../services/storageService";
import { useNavigate, useLocation } from "react-router-dom";
import { UserDto } from '../../models/users/userDto';
import { ConfirmDialog } from './ConfirmDialog';
import { UserUtils } from '../../helpers/userUtils';
import TmBell from '../../icons/TmBell';
import TmExit from '../../icons/TmExit';
import TmGear from '../../icons/TmGear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ErrorAlert from './ErrorAlert';
import { permissionService } from '../../services/permissionService';
import TmEsia from '../../icons/TmEsia';
import { featureTogglesService } from '../../services/featureTogglesService';
import { ROUTES } from '../../constants/routes';
import { RoleType } from '../../models/enums/roleType';

interface HeaderProps {
  title: string;
  hasReturnButton?: boolean;
  notificationCount?: number;
  onBellClick?: () => void;
  onMenuClick?: () => void;
}

const Header = ({ title, hasReturnButton, notificationCount = 0, onBellClick, onMenuClick }: HeaderProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [user, setUser] = useState<UserDto>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [open, setOpen] = React.useState(false);
  const [esiaLoading, setEsiaLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const refreshUser = () => {
      const currentUser = storageService.getUserData();
      if (currentUser) setUser(currentUser);
    };
    refreshUser();
    window.addEventListener('telemed:user-updated', refreshUser);
    return () => window.removeEventListener('telemed:user-updated', refreshUser);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      setEsiaLoading(true);
      authService.linkToEsia(code)
        .then(t => {
          if (t.success) {
            // Refresh user data after successful ESIA login
            const updatedUser = storageService.getUserData();
            if (updatedUser) {
              setUser(updatedUser);
            }
            // Remove the code parameter from URL
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            window.history.replaceState({}, document.title, url.toString());
          } else {
            showAlert(t.message);
          }
        })
        .catch(e => {
          const error = (e as any).response?.data?.message ?? (e as any).message;
          showAlert(error);
        })
        .finally(() => setEsiaLoading(false));
    }
  }, [location.search]);

  const handleMenu = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
    setOpen(false);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    setOpenDialog(false);
  };

  const showAlert = (errorMessage: string = 'Произошла ошибка') => {
    setError(errorMessage);
  }

  const handleEsiaLink = async () => {
    setEsiaLoading(true);
    try {
      const response = await authService.getEsiaLoginUrl();
      if (response.data.success && response.data.data) {
        window.location.href = response.data.data;
      } else {
        showAlert(response.data.message || 'Ошибка получения ссылки для входа через Госуслуги');
      }
    } catch (e) {
      const error = (e as any).response?.data?.message ?? (e as any).message;
      showAlert(error);
    } finally {
      setEsiaLoading(false);
    }
  };

  // Check if user is already linked to ESIA
  const isEsiaLinked = permissionService.isEsiaLinked();

  return (
    <AppBar position="sticky" elevation={0} sx={{
      bgcolor: "rgba(255,255,255,0.96)",
      color: "text.primary",
      borderBottom: 1,
      borderBottomColor: "divider",
      backdropFilter: 'blur(12px)',
      width: { sm: 'calc(100% - 264px)' },
      ml: { sm: '264px' },
      zIndex: (theme) => theme.zIndex.drawer - 1,
    }}>
      <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: { xs: 2, sm: 3, lg: 4 } }}>
        <IconButton
          onClick={onMenuClick}
          color="inherit"
          aria-label="Открыть меню"
          sx={{ mr: 1, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {hasReturnButton &&
          <IconButton onClick={() => navigate(-1)} aria-label="Назад" sx={{ mr: 1, color: 'primary.main', bgcolor: 'primary.light' }}>
              <ChevronLeftIcon />
          </IconButton>
        }

        <Typography variant="h5" component="h1" noWrap sx={{ flexGrow: 1, fontSize: { xs: '1.1rem', sm: '1.35rem' } }}>
          {title}
        </Typography>

        <Box sx={{ color: "text.secondary", display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="large"
            aria-label="Уведомления"
            color="inherit"
            onClick={onBellClick}
          >
            <Badge badgeContent={notificationCount} color="secondary">
              <TmBell />
            </Badge>
          </IconButton>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar
              sx={{ bgcolor: deepPurple[500], width: 36, height: 36 }}
              src={user?.photoUrl}
            >{UserUtils.getInitials(user!)}</Avatar>
            <Typography component="div" sx={{ ml: 1, display: { xs: 'none', md: 'block' }, fontWeight: 600, color: 'text.primary' }}>
              {UserUtils.getShortFio(user!)}
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>{open ? <ExpandLess /> : <ExpandMore />}</Box>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="menu-appbar"
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => navigate(ROUTES.profile)}>
              <Avatar /> Профиль
            </MenuItem>
            {featureTogglesService.isEsiaIntegrationEnabled() && !isEsiaLinked && (
              <MenuItem onClick={handleEsiaLink} disabled={esiaLoading}>
                <TmEsia />
                &nbsp;&nbsp;&nbsp;Привязать к ГосУслугам
              </MenuItem>
            )}
            <Divider />
            {(permissionService.hasRole(RoleType.Admin) || permissionService.hasRole(RoleType.Medic)) && (
              <MenuItem onClick={() => navigate(ROUTES.settings)}>
                <TmGear fill="none" stroke="#1C274C">
                  <Settings fontSize="small" />
                </TmGear>
                &nbsp;&nbsp;&nbsp;Настройки
              </MenuItem>
            )}
            <MenuItem onClick={() => setOpenDialog(true)}>
              <TmExit>
                <Logout fontSize="small" />
              </TmExit>
              &nbsp;&nbsp;&nbsp;Выход
            </MenuItem>
          </Menu>
          <ConfirmDialog
            open={openDialog}
            title="Выход"
            content={
              <>
                Вы действительно хотите выйти?
              </>
            }
            onClose={() => setOpenDialog(false)}
            onConfirm={handleLogout}
          />
        </Box>
      </Toolbar>
      {error && (
        <ErrorAlert
          message={error}
          onClose={() => setError('')}
        />
      )}
    </AppBar>
  );
}

export default Header;
